import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import axiosInstance, { clearAuthData, isJwtAuthFailure } from "../../components/content/axiosConfig";
import { enqueueSnackbar } from "notistack";
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';
import Preloader from "../../components/custom/preLoader";
import ListPagination from "../../components/custom/ListPagination";
import useServerPaginatedList from "../../hooks/useServerPaginatedList";
import showErrorToast, { showSuccessToast } from "../../components/toast/toast.js";
import { openAdminPanelWithAccountScope } from "../../utils/adminHandoff";
import {
  formatDisplayDateShort as formatEndDate,
  formatDisplayDateTime as formatDateTime,
} from "../../utils/formatDisplayDate";

const ACCOUNT_FILTERS = [
    { id: "all", label: "All accounts", shortLabel: "All", subscription: "", status: "" },
    { id: "running", label: "Package running", shortLabel: "Running", subscription: "running", status: "" },
    { id: "trial", label: "On trial", shortLabel: "Trial", subscription: "trial", status: "" },
    { id: "expired", label: "Expired", shortLabel: "Expired", subscription: "expired", status: "" },
    { id: "active", label: "Active", shortLabel: "Active", subscription: "", status: "active" },
    { id: "inactive", label: "Inactive", shortLabel: "Inactive", subscription: "", status: "inactive" },
    { id: "deleted", label: "Deleted accounts", shortLabel: "Deleted", subscription: "", status: "deleted" },
];

const FILTER_STYLE = {
    all: { icon: "fa-users", color: "#495057", bg: "#f8f9fa" },
    running: { icon: "fa-box-open", color: "#0d9488", bg: "#ccfbf1" },
    trial: { icon: "fa-hourglass-half", color: "#4f46e5", bg: "#e0e7ff" },
    expired: { icon: "fa-calendar-times", color: "#dc3545", bg: "#fee2e2" },
    active: { icon: "fa-check-circle", color: "#198754", bg: "#d1e7dd" },
    inactive: { icon: "fa-user-clock", color: "#d97706", bg: "#fef3c7" },
    deleted: { icon: "fa-trash", color: "#6c757d", bg: "#e9ecef" },
};

const SUBSCRIPTION_FILTER_IDS = ["running", "trial", "expired"];
const STATUS_FILTER_IDS = ["active", "inactive", "deleted"];

const cardRadius = { borderRadius: "14px" };

function buildAccountsPath(subscription, status) {
    const params = new URLSearchParams();
    if (subscription) params.set("subscription", subscription);
    if (status) params.set("status", status);
    const qs = params.toString();
    return `/accounts${qs ? `?${qs}` : ""}`;
}

function getFilterById(id) {
    return ACCOUNT_FILTERS.find((f) => f.id === id) || ACCOUNT_FILTERS[0];
}

function getActiveFilterId(subscription, status) {
    const match = ACCOUNT_FILTERS.find(
        (f) => f.subscription === subscription && f.status === status
    );
    return match ? match.id : "all";
}

function AccountFilterPill({ filter, isActive, onSelect }) {
    const style = FILTER_STYLE[filter.id] || FILTER_STYLE.all;
    return (
        <button
            type="button"
            className="accounts-filter-pill"
            aria-pressed={isActive}
            title={filter.label}
            onClick={() => onSelect(filter)}
            style={
                isActive
                    ? {
                          backgroundColor: style.bg,
                          borderColor: style.color,
                          color: style.color,
                          boxShadow: `inset 0 0 0 1px ${style.color}`,
                      }
                    : undefined
            }
        >
            <i className={`fa ${style.icon} fa-fw me-1`} />
            {filter.shortLabel || filter.label}
        </button>
    );
}

function getPackageDisplay(staff) {
    if (staff.PackageName) {
        const full = String(staff.PackageName).trim();
        const dashSplit = full.split(" - ");
        const short =
            dashSplit.length > 1 && dashSplit[0].length >= 3
                ? dashSplit[0].trim()
                : full.length > 28
                  ? `${full.slice(0, 28).trim()}…`
                  : full;
        return { full, short };
    }
    if (!staff.EndDate) return null;
    const end = new Date(staff.EndDate);
    end.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (today <= end) return { full: "Trial", short: "Trial" };
    return null;
}

function PackageCell({ staff }) {
    const pkg = getPackageDisplay(staff);
    if (!pkg) return <span className="text-muted">—</span>;
    const needsTooltip = pkg.full.length > pkg.short.length;
    return (
        <span
            className="accounts-package-cell"
            title={needsTooltip ? pkg.full : undefined}
        >
            {pkg.short}
        </span>
    );
}

const Accounts = () => {
    const location = useLocation();
    const history = useHistory();
    const [deletingId, setDeletingId] = useState(null);
    const [loggingInId, setLoggingInId] = useState(null);
    const [detachingId, setDetachingId] = useState(null);
    const [assigningId, setAssigningId] = useState(null);

    const { subscription, status, activeFilterId, activeFilterLabel } = useMemo(() => {
        const params = new URLSearchParams(location.search);
        const nextSubscription = (params.get("subscription") || "").trim().toLowerCase();
        const nextStatus = (params.get("status") || "").trim().toLowerCase();
        const filterId = getActiveFilterId(nextSubscription, nextStatus);
        const filter = ACCOUNT_FILTERS.find((f) => f.id === filterId) || ACCOUNT_FILTERS[0];
        return {
            subscription: filter.subscription,
            status: filter.status,
            activeFilterId: filter.id,
            activeFilterLabel: filter.label,
        };
    }, [location.search]);

    const fetchStaffPage = useCallback(async ({ search, page, limit }) => {
        try {
            const params = { search, page, limit };
            if (subscription) params.subscription = subscription;
            if (status) params.status = status;
            return await axiosInstance.get(`api/fisher/GetAllFisher`, { params });
        } catch (error) {
            if (isJwtAuthFailure(error)) {
                clearAuthData();
                window.location.href = "/user/login";
                return { data: { data: [], total: 0 } };
            }
            let errorMessage = "An error occurred.";
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            }
            enqueueSnackbar(errorMessage, {
                variant: 'error',
                anchorOrigin: { vertical: 'top', horizontal: 'right' },
                autoHideDuration: 3000,
            });
            throw error;
        }
    }, [subscription, status]);

    const {
        rows: staffs,
        totalCount,
        searchTerm,
        setSearchTerm,
        currentPage,
        setCurrentPage,
        isLoading,
        entriesPerPage,
        refetch,
    } = useServerPaginatedList(fetchStaffPage);

    useEffect(() => {
        setCurrentPage(1);
    }, [subscription, status, setCurrentPage]);

    const applyFilter = (filter) => {
        history.push(buildAccountsPath(filter.subscription, filter.status));
    };

    const loginToAdminPanel = async (fisherId, row) => {
        if (loggingInId != null) return;
        setLoggingInId(fisherId);
        try {
            const token = localStorage.getItem("auth_token");
            const refreshToken = localStorage.getItem("refresh_token");
            let User = null;
            try {
                User = JSON.parse(localStorage.getItem("user_data") || "null");
            } catch {
                User = null;
            }
            if (!token || !User) {
                throw new Error("Super session missing — please log in again");
            }
            openAdminPanelWithAccountScope({
                token,
                refreshToken,
                User,
                accountId: fisherId,
                accountName: row?.FullName || row?.FisherName || null,
                accountEmail: row?.Email || null,
            });
            showSuccessToast("Opening Admin panel…");
        } catch (error) {
            if (isJwtAuthFailure(error)) {
                clearAuthData();
                window.location.href = "/user/login";
                return;
            }
            showErrorToast(error.response?.data?.message || error.message || "Could not open Admin panel");
        } finally {
            setLoggingInId(null);
        }
    };

    const updateStatus = async (staffId) => {
        try {
            await axiosInstance.post(`api/fisher/ChangeStatus/${staffId}`);
            await refetch();
        } catch (error) {
            if (isJwtAuthFailure(error)) {
                clearAuthData();
                window.location.href = "/user/login";
            }
        }
    };

    function formatPackageLabel(staff) {
        return <PackageCell staff={staff} />;
    }

    const DeleteUser = async (staffId) => {
        if (deletingId != null || detachingId != null) return;

        let result;
        try {
            result = await Swal.fire({
                title: "Delete this account?",
                html: `
                  <p class="text-start small mb-2">Choose what to do with pond and fish data:</p>
                  <ul class="text-start small mb-0">
                    <li><strong>Keep data</strong> — remove login and personal info; ponds/fish stay under a Deleted account.</li>
                    <li><strong>Delete everything</strong> — permanently wipe the account and all pond/fish data.</li>
                  </ul>
                `,
                icon: "warning",
                showDenyButton: true,
                showCancelButton: true,
                confirmButtonText: "Keep data, delete account",
                denyButtonText: "Delete everything",
                cancelButtonText: "Cancel",
                confirmButtonColor: "#0d9488",
                denyButtonColor: "#d33",
                cancelButtonColor: "#6c757d",
                focusCancel: true,
            });
        } catch (error) {
            showErrorToast("Could not open the confirmation dialog.");
            console.error("Delete confirmation error:", error);
            return;
        }

        if (result.isDismissed) {
            return;
        }

        const mode = result.isConfirmed ? "keep" : "wipe";

        setDeletingId(staffId);
        try {
            const res = await axiosInstance.post(`api/fisher/CloseAccount/${staffId}`, {
                confirm: true,
                mode,
            });
            showSuccessToast(
                res.data?.message ||
                    (mode === "keep"
                        ? "Account closed; pond and fish data kept"
                        : "Account and all data permanently deleted")
            );
            await refetch();
        } catch (error) {
            if (isJwtAuthFailure(error)) {
                clearAuthData();
                window.location.href = "/user/login";
                return;
            }
            const errorMessage =
                error.response?.data?.message || "Failed to delete account. Please try again.";
            showErrorToast(errorMessage);
            console.error("Error deleting fisher:", error);
        } finally {
            setDeletingId(null);
        }
    };

    const detachFromCompany = async (staff) => {
        if (deletingId != null || detachingId != null || assigningId != null) return;
        if (!staff?.ManagedByCompanyName && !staff?.ManagedByCompanyID) return;

        const companyLabel = staff.ManagedByCompanyName || "their consultancy";
        let result;
        try {
            result = await Swal.fire({
                title: "Remove from consultancy?",
                html: `<p class="mb-2">Remove <strong>${staff.FullName}</strong> from <strong>${companyLabel}</strong>?</p>
                  <p class="mb-0 text-muted small">They keep their ponds and data, and will manage their own subscription going forward. Company billing for this account will stop.</p>`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#0d9488",
                cancelButtonColor: "#6c757d",
                confirmButtonText: "Yes, remove from consultancy",
                cancelButtonText: "Cancel",
                focusCancel: true,
            });
        } catch (error) {
            showErrorToast("Could not open the confirmation dialog.");
            console.error("Detach confirmation error:", error);
            return;
        }
        if (!result.isConfirmed) return;

        setDetachingId(staff.id);
        try {
            const res = await axiosInstance.post(
                `api/fisher/DetachFromCompany?id=${staff.id}`
            );
            showSuccessToast(
                res.data?.message ||
                    "Account removed from consultancy. They now manage their own subscription."
            );
            await refetch();
        } catch (error) {
            if (isJwtAuthFailure(error)) {
                clearAuthData();
                window.location.href = "/user/login";
                return;
            }
            showErrorToast(
                error.response?.data?.message ||
                    "Failed to remove account from consultancy"
            );
        } finally {
            setDetachingId(null);
        }
    };

    const assignToCompany = async (staff) => {
        if (deletingId != null || detachingId != null || assigningId != null) return;
        if (staff?.ManagedByCompanyID || staff?.ManagedByCompanyName) {
            showErrorToast("This account is already managed by a consultancy");
            return;
        }

        setAssigningId(staff.id);
        try {
            const listRes = await axiosInstance.get("api/company/GetAll", {
                params: { page: 1, limit: 200 },
            });
            const companies = Array.isArray(listRes.data?.data)
                ? listRes.data.data
                : Array.isArray(listRes.data)
                  ? listRes.data
                  : [];
            const eligible = companies.filter(
                (c) =>
                    c.Status &&
                    !c.ConvertedAt &&
                    !c.ConvertedToFisherID
            );
            if (eligible.length === 0) {
                showErrorToast("No active companies available. Create one under Companies first.");
                return;
            }

            const options = {};
            eligible.forEach((c) => {
                options[String(c.id)] = `${c.Name} (${c.Email})`;
            });

            const pick = await Swal.fire({
                title: `Assign ${staff.FullName}`,
                html: `<p class="text-start small mb-2">Link this pond owner to a consultancy immediately. Package and end date stay the same; the company handles renewals.</p>`,
                input: "select",
                inputOptions: options,
                inputPlaceholder: "Choose company…",
                showCancelButton: true,
                confirmButtonText: "Assign",
                cancelButtonText: "Cancel",
                confirmButtonColor: "#0d9488",
                inputValidator: (value) => {
                    if (!value) return "Select a company";
                    return null;
                },
            });
            if (!pick.isConfirmed || !pick.value) return;

            const res = await axiosInstance.post(
                `api/company/${pick.value}/attach-account`,
                { fisherId: staff.id }
            );
            showSuccessToast(res.data?.message || "Account assigned to company");
            await refetch();
        } catch (error) {
            if (isJwtAuthFailure(error)) {
                clearAuthData();
                window.location.href = "/user/login";
                return;
            }
            showErrorToast(
                error.response?.data?.message || "Failed to assign account to company"
            );
        } finally {
            setAssigningId(null);
        }
    };

    if (
        isLoading &&
        staffs.length === 0 &&
        deletingId == null &&
        detachingId == null &&
        assigningId == null
    ) {
        return <Preloader />;
    }

    return (
        <div className="accounts-page">
            <style>{`
                .accounts-page {
                    display: flex;
                    flex-direction: column;
                    min-height: calc(100vh - 6.5rem);
                }
                .accounts-table-card {
                    flex: 1 1 auto;
                    display: flex;
                    flex-direction: column;
                    min-height: 28rem;
                }
                .accounts-table-card > .card-body {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    min-height: 0;
                }
                .accounts-table-scroll {
                    flex: 1 1 auto;
                    min-height: 22rem;
                    overflow: auto;
                }
                .accounts-filter-toolbar {
                    background: #fff;
                    border: 1px solid #e9ecef;
                    border-radius: 14px;
                }
                .accounts-filter-pill {
                    display: inline-flex;
                    align-items: center;
                    padding: 0.3rem 0.65rem;
                    font-size: 0.75rem;
                    font-weight: 500;
                    line-height: 1;
                    color: #495057;
                    background: #fff;
                    border: 1px solid #dee2e6;
                    border-radius: 999px;
                    transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
                }
                .accounts-filter-pill .fa {
                    font-size: 0.7rem;
                }
                .accounts-filter-pill:hover {
                    background: #f8f9fa;
                    border-color: #ced4da;
                }
                .accounts-filter-pill:focus-visible {
                    outline: 2px solid #86b7fe;
                    outline-offset: 2px;
                }
                .accounts-filter-divider {
                    width: 1px;
                    height: 1.25rem;
                    background: #dee2e6;
                    flex-shrink: 0;
                }
                .accounts-search-input {
                    border-radius: 8px !important;
                    min-height: 34px;
                    font-size: 0.875rem;
                }
                .accounts-package-cell {
                    display: inline-block;
                    max-width: 11rem;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    vertical-align: middle;
                    cursor: default;
                }
                .accounts-table th.col-package,
                .accounts-table td.col-package {
                    max-width: 11rem;
                }
                .accounts-count-badge {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    min-width: 2rem;
                    height: 2rem;
                    padding: 0 0.65rem;
                    border-radius: 999px;
                    font-size: 0.875rem;
                    font-weight: 600;
                    line-height: 1;
                    color: #374151;
                    background: #f3f4f6;
                    border: 1px solid #e5e7eb;
                }
            `}</style>

            <div className="d-flex align-items-center mb-3">
                <div>
                    <ul className="breadcrumb mb-2">
                        <li className="breadcrumb-item">
                            <Link to="/dashboard">Home</Link>
                        </li>
                        <li className="breadcrumb-item active">Accounts</li>
                    </ul>
                    <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                        <h1 className="page-header mb-0">Accounts</h1>
                        <span className="accounts-count-badge" aria-label="Total accounts">
                            {isLoading ? "…" : totalCount}
                        </span>
                        {!isLoading && activeFilterId !== "all" ? (
                            <span
                                className="small fw-medium"
                                style={{ color: FILTER_STYLE[activeFilterId]?.color }}
                            >
                                {activeFilterLabel}
                            </span>
                        ) : null}
                    </div>
                    <p className="text-muted small mb-0">
                        Customer accounts across the platform — filter by subscription or account status.
                    </p>
                </div>
                <div className="ms-auto" />
            </div>

            <div className="card border-0 shadow-sm mb-3 accounts-filter-toolbar" style={cardRadius}>
                <div className="card-body py-2 px-3">
                    <div className="d-flex flex-wrap align-items-center gap-2">
                        <div className="d-flex flex-wrap align-items-center gap-1">
                            <AccountFilterPill
                                filter={getFilterById("all")}
                                isActive={activeFilterId === "all"}
                                onSelect={applyFilter}
                            />
                            <span className="accounts-filter-divider" aria-hidden="true" />
                            {SUBSCRIPTION_FILTER_IDS.map((id) => (
                                <AccountFilterPill
                                    key={id}
                                    filter={getFilterById(id)}
                                    isActive={activeFilterId === id}
                                    onSelect={applyFilter}
                                />
                            ))}
                            <span className="accounts-filter-divider" aria-hidden="true" />
                            {STATUS_FILTER_IDS.map((id) => (
                                <AccountFilterPill
                                    key={id}
                                    filter={getFilterById(id)}
                                    isActive={activeFilterId === id}
                                    onSelect={applyFilter}
                                />
                            ))}
                        </div>

                        <div
                            className="position-relative flex-grow-1 ms-md-auto"
                            style={{ minWidth: "160px", maxWidth: "280px" }}
                        >
                            <div
                                className="position-absolute top-50 translate-middle-y text-muted ps-2"
                                style={{ zIndex: 2, fontSize: "0.8rem" }}
                            >
                                <i className="fa fa-search opacity-50" />
                            </div>
                            <input
                                type="search"
                                className="form-control form-control-sm accounts-search-input border bg-light ps-4"
                                placeholder="Search name or email…"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        <div className="d-flex align-items-center gap-2 ms-auto ms-md-0 text-nowrap">
                            {activeFilterId !== "all" || searchTerm ? (
                                <Link
                                    to="/accounts"
                                    className="small text-decoration-none"
                                    onClick={() => setSearchTerm("")}
                                >
                                    Clear
                                </Link>
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>

            <div className="card border-0 shadow-sm accounts-table-card" style={cardRadius}>
                <div className="card-body p-0">
                    <div className="table-responsive mb-0 position-relative accounts-table-scroll">
                            {deletingId != null || detachingId != null || assigningId != null ? (
                                <div
                                    className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-75"
                                    style={{ zIndex: 5 }}
                                >
                                    <div className="text-muted">
                                        <i className="fa fa-spinner fa-spin me-2" />
                                        {assigningId != null
                                            ? "Assigning to consultancy…"
                                            : detachingId != null
                                            ? "Removing from consultancy…"
                                            : "Deleting account…"}
                                    </div>
                                </div>
                            ) : null}
                            <table className="table table-hover table-panel align-middle mb-0 accounts-table">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Email</th>
                                        <th className="col-package">Package</th>
                                        <th>Pond license</th>
                                        <th>MRR</th>
                                        <th>Sub ends</th>
                                        <th># Ponds</th>
                                        <th># Fishes</th>
                                        <th>Fish Types</th>
                                        <th>Sampled</th>
                                        <th>Status</th>
                                        <th>Created At</th>
                                        <th>Actions </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isLoading ? (
                                        <tr>
                                            <td colSpan={13} className="text-center text-muted py-5">
                                                Loading…
                                            </td>
                                        </tr>
                                    ) : staffs.length === 0 ? (
                                        <tr>
                                            <td colSpan={13} className="text-center text-muted py-5">
                                                No accounts match this filter.
                                            </td>
                                        </tr>
                                    ) : (
                                        staffs.map((staff) => (
                                            <tr key={staff.id}>
                                                <td>
                                                    {staff.FullName}
                                                    {staff.ManagedByCompanyName ? (
                                                        <span
                                                            className="badge bg-info bg-opacity-10 text-info border border-info border-opacity-25 ms-2"
                                                            title={`Managed by ${staff.ManagedByCompanyName}`}
                                                        >
                                                            {staff.ManagedByCompanyName}
                                                        </span>
                                                    ) : null}
                                                </td>
                                                <td>{staff.Email}</td>
                                                <td className="col-package">{formatPackageLabel(staff)}</td>
                                                <td>{staff.LicensedPondCount ?? 1}</td>
                                                <td>
                                                    {staff.CurrentMrr != null
                                                        ? `$${Number(staff.CurrentMrr).toFixed(2)}`
                                                        : '—'}
                                                </td>
                                                <td>{formatEndDate(staff.EndDate)}</td>
                                                <td>{staff.NumberOfPonds || 0}</td>
                                                <td>{staff.NumberOfFishes || 0}</td>
                                                <td>{staff.NumberOfFishTypes || 0}</td>
                                                <td>{staff.NumberOfFishesSampled || 0}</td>
                                                <td
                                                    onClick={() => {
                                                        if (!staff.DeletedAt) updateStatus(staff.id);
                                                    }}
                                                    style={!staff.DeletedAt ? { cursor: "pointer" } : undefined}
                                                >
                                                    {staff.DeletedAt ? (
                                                        <span className="badge border border-secondary text-secondary px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center">
                                                            <i className="fa fa-circle fs-9px fa-fw me-5px"></i>{" "}
                                                            Deleted
                                                        </span>
                                                    ) : staff.Status ? (
                                                        <span className="badge border border-success text-success px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center">
                                                            <i className="fa fa-circle fs-9px fa-fw me-5px"></i>{" "}
                                                            Active
                                                        </span>
                                                    ) : (
                                                        <span className="badge border border-danger text-warning px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center">
                                                            <i className="fa fa-circle fs-9px fa-fw me-5px"></i>{" "}
                                                            Pending
                                                        </span>
                                                    )}
                                                </td>
                                                <td>{formatDateTime(staff.createdAt)}</td>
                                                <td>
                                                    {staff.DeletedAt ? (
                                                        <span className="small text-muted">Closed</span>
                                                    ) : (
                                                    <>
                                                    <span>
                                                        <button
                                                            type="button"
                                                            className="btn btn-link p-0 border-0 text-primary"
                                                            title="Open account (sampling)"
                                                            disabled={loggingInId != null || deletingId != null || detachingId != null || assigningId != null}
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                loginToAdminPanel(staff.id, staff);
                                                            }}
                                                        >
                                                            <i className="fa fa-sign-in fs-13px fa-fw me-5px" />
                                                        </button>
                                                    </span>
                                                    <span>
                                                        <Link to={"/updateFisher/" + staff.id}>
                                                            {" "}
                                                            <i className="fa fa-pencil fs-13px fa-fw me-5px"></i>
                                                        </Link>
                                                    </span>
                                                    <span>
                                                        <Link to={"/staff/" + staff.id}>
                                                            {" "}
                                                            <i className="fa fa-user fs-13px fa-fw me-5px"></i>
                                                        </Link>
                                                    </span>
                                                    {staff.ManagedByCompanyName ? (
                                                        <span>
                                                            <button
                                                                type="button"
                                                                className="btn btn-link p-0 border-0"
                                                                title={`Remove from ${staff.ManagedByCompanyName}`}
                                                                style={{ color: "#0d9488" }}
                                                                disabled={deletingId != null || detachingId != null || assigningId != null}
                                                                onClick={(event) => {
                                                                    event.stopPropagation();
                                                                    detachFromCompany(staff);
                                                                }}
                                                            >
                                                                <i className="fa fa-unlink fs-13px fa-fw me-5px" />
                                                            </button>
                                                        </span>
                                                    ) : (
                                                        <span>
                                                            <button
                                                                type="button"
                                                                className="btn btn-link p-0 border-0"
                                                                title="Assign to consultancy"
                                                                style={{ color: "#0d9488" }}
                                                                disabled={deletingId != null || detachingId != null || assigningId != null}
                                                                onClick={(event) => {
                                                                    event.stopPropagation();
                                                                    assignToCompany(staff);
                                                                }}
                                                            >
                                                                <i className="fa fa-link fs-13px fa-fw me-5px" />
                                                            </button>
                                                        </span>
                                                    )}
                                                    &nbsp;&nbsp;
                                                    <button
                                                        type="button"
                                                        className="btn btn-link p-0 border-0 text-danger"
                                                        title="Delete account"
                                                        disabled={deletingId != null || detachingId != null || assigningId != null}
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            DeleteUser(staff.id);
                                                        }}
                                                    >
                                                        <i className="fa fa-trash fs-13px fa-fw me-5px" />
                                                    </button>
                                                    </>
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <ListPagination
                            currentPage={currentPage}
                            totalCount={totalCount}
                            entriesPerPage={entriesPerPage}
                            onPageChange={setCurrentPage}
                            className="px-3 pb-3"
                        />
                </div>
            </div>
        </div>
    );
};

export default Accounts;
